"use client";

import Link from "@/components/ui/CcLink";
import { usePathname } from "next/navigation";
import { useCallback, useEffect, useId, useRef, useState } from "react";

import { localeFromPathname, localizeInternalHref } from "@/lib/i18n/paths";

import { getCookieConsentCopy } from "@/lib/cookie-consent-copy";
import {
  COOKIE_CONSENT_OPEN_EVENT,
  readCookieConsent,
  type CookieConsentOpenDetail,
  type CookieConsentInitialState,
  mirrorCookieConsent,
  writeCookieConsent,
} from "@/lib/cookie-consent";

const PRIVACY_COOKIES_HREF = "/privacy-policy#cookies";

function syncDraftAnalyticsFromStorage(setDraftAnalytics: (value: boolean) => void) {
  const stored = readCookieConsent();
  setDraftAnalytics(stored?.categories.analytics ?? false);
}

export function CcCookieConsent({ initialConsent }: { initialConsent?: CookieConsentInitialState | null }) {
  // Rendered in the root layout, outside the locale provider, so read the language from the URL.
  const locale = localeFromPathname(usePathname() ?? "/");
  const copy = getCookieConsentCopy(locale);
  const privacyCookiesHref = localizeInternalHref(PRIVACY_COOKIES_HREF, locale);
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(initialConsent !== undefined);
  const [open, setOpen] = useState(initialConsent === null);
  const [manageOpen, setManageOpen] = useState(false);
  const [draftAnalytics, setDraftAnalytics] = useState(initialConsent?.categories.analytics ?? false);
  const [visible, setVisible] = useState(initialConsent === null);

  const showBanner = useCallback((options?: CookieConsentOpenDetail) => {
    syncDraftAnalyticsFromStorage(setDraftAnalytics);
    setManageOpen(options?.manage ?? false);
    setOpen(true);
    requestAnimationFrame(() => setVisible(true));
  }, []);

  useEffect(() => {
    setMounted(true);
    const storedRecord = readCookieConsent();
    const stored = storedRecord ?? initialConsent;
    if (!stored) {
      showBanner({ manage: false });
      return;
    }
    setOpen(false);
    setVisible(false);
    setDraftAnalytics(stored.categories.analytics);
    if (!initialConsent && storedRecord) mirrorCookieConsent(storedRecord);
  }, [showBanner, initialConsent]);

  useEffect(() => {
    const handleOpen = (event: Event) => {
      const custom = event as CustomEvent<CookieConsentOpenDetail>;
      showBanner(custom.detail);
    };

    window.addEventListener(COOKIE_CONSENT_OPEN_EVENT, handleOpen);
    return () => window.removeEventListener(COOKIE_CONSENT_OPEN_EVENT, handleOpen);
  }, [showBanner]);

  const persist = useCallback((analytics: boolean) => {
    writeCookieConsent({ analytics });
    setDraftAnalytics(analytics);
    setManageOpen(false);
    setVisible(false);
    window.setTimeout(() => setOpen(false), 320);
  }, []);

  const handleAcceptAll = () => persist(true);
  const handleSavePreferences = () => persist(draftAnalytics);

  if (!mounted || !open) return null;

  return (
    <div
      ref={panelRef}
      className={`cc-cookie-consent${visible ? " cc-cookie-consent--visible" : ""}${manageOpen ? " cc-cookie-consent--manage" : ""}`}
      role="dialog"
      aria-modal="false"
      aria-labelledby={titleId}
      data-cc-cursor-ignore
    >
      <div className="cc-cookie-consent__inner">
        {manageOpen ? (
          <>
            <div className="cc-cookie-consent__copy">
              <p id={titleId} className="cc-cookie-consent__title">
                {copy.manage.title}
              </p>
              <p className="cc-cookie-consent__text">
                {copy.manage.body}{" "}
                <Link href={privacyCookiesHref} className="cc-cookie-consent__link">
                  {copy.banner.privacyLink}
                </Link>
              </p>
            </div>
            <div className="cc-cookie-consent__manage">
              <ul className="cc-cookie-consent__categories" role="list">
                <li className="cc-cookie-consent__category">
                  <div className="cc-cookie-consent__category-head">
                    <span className="cc-cookie-consent__category-name">{copy.manage.necessary}</span>
                    <span className="cc-cookie-consent__badge">{copy.manage.alwaysOn}</span>
                  </div>
                  <p className="cc-cookie-consent__category-desc">{copy.manage.necessaryDesc}</p>
                </li>
                <li className="cc-cookie-consent__category">
                  <div className="cc-cookie-consent__category-head">
                    <label className="cc-cookie-consent__toggle" htmlFor="cc-cookie-analytics">
                      <span className="cc-cookie-consent__category-name">{copy.manage.analytics}</span>
                      <input
                        id="cc-cookie-analytics"
                        type="checkbox"
                        className="cc-cookie-consent__toggle-input"
                        checked={draftAnalytics}
                        onChange={(event) => setDraftAnalytics(event.target.checked)}
                      />
                      <span className="cc-cookie-consent__toggle-ui" aria-hidden="true" />
                      <span className="cc-cookie-consent__sr-only">{copy.manage.analyticsSr}</span>
                    </label>
                  </div>
                  <p className="cc-cookie-consent__category-desc">{copy.manage.analyticsDesc}</p>
                </li>
              </ul>
              <div className="cc-cookie-consent__actions cc-cookie-consent__actions--manage">
                <button
                  type="button"
                  className="cc-cookie-consent__btn cc-cookie-consent__btn--secondary"
                  onClick={() => setManageOpen(false)}
                >
                  {copy.manage.back}
                </button>
                <button
                  type="button"
                  className="cc-cookie-consent__btn cc-cookie-consent__btn--primary"
                  onClick={handleSavePreferences}
                >
                  {copy.manage.save}
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="cc-cookie-consent__banner">
            <div className="cc-cookie-consent__content">
              <p id={titleId} className="cc-cookie-consent__heading">
                <span className="cc-cookie-consent__icon" aria-hidden="true">🍪</span>
                {copy.banner.title}
              </p>
              <p className="cc-cookie-consent__text">{copy.banner.body}</p>
            </div>
            <div className="cc-cookie-consent__aside">
              <button
                type="button"
                className="cc-cookie-consent__btn cc-cookie-consent__btn--primary"
                onClick={handleAcceptAll}
              >
                {copy.banner.acceptAll}
              </button>
              <button
                type="button"
                className="cc-cookie-consent__manage-link"
                onClick={() => setManageOpen(true)}
              >
                {copy.banner.managePreferences}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
